import axios from "axios";
import * as Sentry from "@sentry/react";
import { v4 as uuidv4 } from "uuid"; // Install: npm install uuid
import { logErrorWithPayload } from "./logService";
import { clearAuthToken, getAuthHeader, isLoggingOut } from "./authToken";
import {
  ErrorType,
  MISSING_QUERY_PARAM_ERROR_CODE,
  isErrorLogRequest,
  reportRequestFailure,
} from "./errorLogService";
import config from "../config";

const { localurl, reportanalyticsurl } = config;

// Generate correlation ID for error tracking
const generateCorrelationId = () => {
  return `ERR-${Date.now()}-${uuidv4()
    .substring(0, 8)
    .toUpperCase()}`;
};

// Get user-friendly error details
const getUserFriendlyError = (status, error) => {
  const correlationId = generateCorrelationId();

  // The 42Crunch firewall's rejection was readable (it carried CORS headers).
  if (getApiFirewallRejection(error)) {
    return {
      code: status?.toString() || "400",
      title: "Some information couldn't be accepted",
      message:
        "Part of the information entered isn't in a format we can accept. Please check what you entered (for example special characters or very long text) and try again.",
      correlationId,
      shouldClearAuth: false,
    };
  }

  // No response at all. axios reports both cases as ERR_NETWORK because the
  // browser hides the real cause, so tell them apart by whether we're online.
  if (!error?.response && error?.code === "ERR_NETWORK") {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return {
        code: "NETWORK",
        title: "No internet connection",
        message:
          "Your internet is not working. Please check your connection and try again.",
        correlationId,
        shouldClearAuth: false,
      };
    }

    // Online but no readable response: usually a request rejected by the API
    // firewall (its rejection has no CORS headers, so the browser reports a
    // CORS error), otherwise the server couldn't be reached.
    return {
      code: "BLOCKED",
      title: "Request couldn't be completed",
      message:
        "Some information is invalid, missing or too long. Please check your details and try again.If the issue continues, please contact support.",
      correlationId,
      shouldClearAuth: false,
    };
  }

  // Default fallback for unknown errors
  return {
    code: status?.toString() || "UNKNOWN",
    message: error.response?.data?.message || error.message,
    correlationId,
    shouldClearAuth: false,
  };
};

// Navigate to error page helper
const navigateToErrorPage = (errorInfo) => {
  const errorState = {
    code: errorInfo.code,
    title: errorInfo.title,
    message: errorInfo.message,
    correlationId: errorInfo.correlationId,
    timestamp: new Date().toISOString(),
  };

  // Store in sessionStorage for error page to read
  sessionStorage.setItem("lastError", JSON.stringify(errorState));

  // Dispatch custom event for App component to handle navigation
  window.dispatchEvent(
    new CustomEvent("navigateToError", {
      detail: errorState,
    }),
  );
};

// Safely parse a JSON string; returns the original value if it is not a JSON string.
const tryParseJson = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch (_) {
    return value;
  }
};

// The 42Crunch API firewall rejects requests before they reach the API and
// answers with its own problem body ({ status, title: "request validation",
// detail, uuid }) instead of the API's { success, message, problem } envelope.
const getApiFirewallRejection = (error) => {
  const body = tryParseJson(error?.response?.data);
  if (!body || typeof body !== "object" || "success" in body) return null;

  const title = typeof body.title === "string" ? body.title : "";
  const looksLikeFirewall =
    /(request|response) validation/i.test(title) ||
    (typeof body.uuid === "string" && typeof body.status === "number" && title);

  return looksLikeFirewall
    ? { title, detail: body.detail, uuid: body.uuid }
    : null;
};

const safeDecodeURIComponent = (value) => {
  try {
    return decodeURIComponent(value);
  } catch (_) {
    // A literal "%" that isn't an escape sequence (e.g. "50%").
    return value;
  }
};

// Most URLs in apiUrls.js interpolate values straight into the query string, so
// a "+" in an email arrives as a space and a "#" cuts the query off. Re-encode
// every value; decoding first keeps already-encoded values unchanged. A raw "+"
// is kept as a literal plus, never treated as a space.
const encodeQueryString = (url) => {
  const queryIndex = (url || "").indexOf("?");
  if (queryIndex === -1) return url;
  const query = url
    .slice(queryIndex + 1)
    .split("&")
    .map((pair) => {
      const separator = pair.indexOf("=");
      if (separator === -1) return pair;
      const key = pair.slice(0, separator);
      const value = safeDecodeURIComponent(pair.slice(separator + 1));
      return `${key}=${encodeURIComponent(value)}`;
    })
    .join("&");
  return `${url.slice(0, queryIndex)}?${query}`;
};

// Build a synthetic Error whose stack points to the application call site rather
// than Axios internals. The AxiosError thrown by `settle` contains no useful
// frames — by the time the response interceptor runs, the async context has
// unwound and the original caller (component, service, etc.) is gone from the
// stack. We work around this by capturing the stack eagerly in the request
// interceptor (still synchronous, so the full app call chain is intact) and
// replaying it here on the error object that goes to Sentry.
const buildCallerError = (callerStack, method, url, status, serverMessage) => {
  const label = `[${status}] ${(method || "HTTP").toUpperCase()} ${url}`;
  const message = serverMessage ? `${label}: ${serverMessage}` : label;

  const err = new Error(message);
  err.name = "ApiError";

  if (callerStack) {
    // Replace the first line (the Error() constructor text) with our label so
    // the Sentry title is readable, then keep all remaining frames as-is.
    const lines = callerStack.split("\n");
    lines[0] = `ApiError: ${label}`;
    err.stack = lines.join("\n");
  }

  return err;
};

// Query values that come from unset state ("?pharmacyID=undefined") are always
// rejected by the 42Crunch firewall, so find them before the request is sent.
// (axios already omits `params` entries that are undefined.)
const PLACEHOLDER_QUERY_VALUES = ["undefined", "null", "NaN", "Invalid Date"];

const findMissingQueryParams = (config) => {
  const missing = [];
  const queryIndex = (config.url || "").indexOf("?");
  if (queryIndex !== -1) {
    const query = new URLSearchParams(config.url.slice(queryIndex + 1));
    query.forEach((value, key) => {
      if (PLACEHOLDER_QUERY_VALUES.includes(value)) missing.push(key);
    });
  }
  Object.entries(config.params || {}).forEach(([key, value]) => {
    if (
      value !== undefined &&
      PLACEHOLDER_QUERY_VALUES.includes(String(value))
    ) {
      missing.push(key);
    }
  });
  return missing;
};

// Most URLs in apiUrls.js interpolate values straight into the query string, so
// a "+" in an email arrives as a space and a "#" cuts the query off. Re-encode
// every value; decoding first keeps already-encoded values unchanged. A raw "+"
// is kept as a literal plus, never treated as a space.

const createInstance = (baseURL) => {
  const instance = axios.create({ baseURL });

  // Request interceptor
  instance.interceptors.request.use((config) => {
    const authHeader = getAuthHeader();

    if (authHeader) {
      config.headers.Authorization = authHeader;
    } else {
      // Not logged in yet (or already logged out): the app is about to redirect
      // to the login screen, so log failures but don't show the error modal.
      config.skipErrorPage = true;
    }

    // Capture the application call stack NOW, before the request goes async.
    // Axios request interceptors run synchronously so the full call chain —
    // including the component or service that initiated the request — is
    // still on the stack at this point. By the time the response error
    // interceptor fires, that context is completely gone.
    config.__sentryCallerStack = new Error().stack;

    // errorLogService posts with plain axios, but if the error-log endpoint is
    // ever called through this instance its failure must never open the error popup.
    if (isErrorLogRequest(config.url)) {
      config.skipErrorPage = true;
    }

    const missingQueryParams = findMissingQueryParams(config);
    if (missingQueryParams.length > 0) {
      // Usually a call made before the store has the id/email loaded. Fail it
      // here (logged via the response interceptor with the caller stack)
      // instead of sending a request the API firewall will reject.
      config.skipErrorPage = true;
      const error = new Error(
        `Request blocked: missing value for query parameter(s) ${missingQueryParams.join(
          ", ",
        )}`,
      );
      error.config = config;
      error.code = MISSING_QUERY_PARAM_ERROR_CODE;
      return Promise.reject(error);
    }

    config.url = encodeQueryString(config.url);

    // Add an HTTP breadcrumb so the Sentry event's Breadcrumbs trail shows
    // every API call that preceded the error, not just the failing one.
    Sentry.addBreadcrumb({
      type: "http",
      category: "fetch",
      message: `${(config.method || "GET").toUpperCase()} ${config.url}`,
      level: "info",
      data: {
        url: `${baseURL}${config.url}`,
        method: (config.method || "GET").toUpperCase(),
      },
    });

    return config;
  });

  // Response interceptor
  instance.interceptors.response.use(
    (res) => {
      let result = res && res.data ? res.data : res;
      if (
        res?.config?.responseType === "blob" ||
        res?.config?.responseType === "arraybuffer"
      ) {
        return Promise.resolve(result);
      }
      if (result?.success === false) {
        if (isLoggingOut()) {
          return Promise.reject(result);
        }
        // Even success:false responses should show error page
        const errorInfo = {
          code: res.status?.toString() || "ERROR",
          title: "Request Failed",
          message: result?.message || "The request could not be completed.",
          correlationId: generateCorrelationId(),
          shouldClearAuth: false,
        };

        logErrorWithPayload(new Error(result?.message || "Request failed"), {
          correlationId: errorInfo.correlationId,
          status: res.status,
          responseData: result,
          route: window.location.pathname,
          timestamp: new Date().toISOString(),
        });

        reportRequestFailure({
          requestConfig: res.config,
          status: res.status,
          responseData: result,
          errorType: ErrorType.RequestFailed,
          correlationId: errorInfo.correlationId,
        });

        if (res?.config?.skipErrorPage !== true) {
          navigateToErrorPage(errorInfo);
        }

        return Promise.reject(result);
      } else {
        result = result?.response || null;
      }
      return Promise.resolve(result);
    },
    (error) => {
      const status = error?.response?.status || -1;
      const requestConfig = error?.config || {};
      const requestHeaders = { ...(requestConfig.headers || {}) };

      // Redact authorization header for logging
      if (requestHeaders.Authorization) {
        requestHeaders.Authorization = "[REDACTED]";
      }

      // Axios serializes the request body to a JSON string; parse it back so Sentry
      // shows the actual object rather than a double-encoded escaped string.
      const parsedRequestData = tryParseJson(requestConfig.data);

      // Get user-friendly error info. A bug here must not stop the logging and
      // rejection below, so fall back to a generic message.
      let errorInfo;
      try {
        errorInfo = getUserFriendlyError(status, error);
      } catch (_) {
        errorInfo = {
          code: status?.toString() || "UNKNOWN",
          message: error?.message || "The request could not be completed.",
          correlationId: generateCorrelationId(),
          shouldClearAuth: false,
        };
      }

      // Build a synthetic error whose stack traces back to the component/service
      // that made the request, not into Axios `settle` internals.
      const serverMessage =
        error?.response?.data?.message || error?.response?.data?.title;
      const firewallRejection = getApiFirewallRejection(error);
      const callerError = buildCallerError(
        requestConfig.__sentryCallerStack,
        requestConfig.method,
        requestConfig.url,
        status,
        firewallRejection
          ? `42Crunch ${firewallRejection.title || "rejection"}`
          : serverMessage,
      );
      if (firewallRejection) callerError.name = "ApiFirewallError";

      // Log with full API context so Sentry surfaces method, endpoint, status,
      // request body, and response body as structured data rather than a flat string.
      logErrorWithPayload(callerError, {
        correlationId: errorInfo.correlationId,
        status,
        method: requestConfig.method,
        url: requestConfig.url,
        baseURL: requestConfig.baseURL,
        params: requestConfig.params,
        data: parsedRequestData,
        headers: requestHeaders,
        responseData: error?.response?.data,
        route: window.location.pathname,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        // Firewall rejections need the exact bytes that were sent to find the
        // field that broke the OpenAPI contract, so keep the unparsed body too.
        ...(firewallRejection && {
          apiFirewall: firewallRejection,
          rawRequestBody: requestConfig.data,
        }),
      });

      // Mark the original error so component-level catch handlers know it has
      // already been sent to Sentry. Without this, a component that wraps the
      // call in try/catch and calls logError(err) creates a second, duplicate
      // event — and that second event is the one that shows the JSON blob title
      // because it goes through the generic logWithLevel path.
      error.__sentryLogged = true;

      reportRequestFailure({
        requestConfig,
        status,
        error,
        responseData: tryParseJson(error?.response?.data),
        correlationId: errorInfo.correlationId,
      });

      // Clear auth token if needed (for 401 errors)
      if (errorInfo.shouldClearAuth) {
        clearAuthToken();
      }

      if (requestConfig.skipErrorPage !== true && !isLoggingOut()) {
        navigateToErrorPage(errorInfo);
      }

      // Attach correlation ID to error for component-level handling if needed
      error.correlationId = errorInfo.correlationId;
      error.userFriendlyMessage = errorInfo.message;

      return Promise.reject(error);
    },
  );

  return instance;
};

const localInstance = createInstance(localurl);
const reportAnalyticsInstance = createInstance(reportanalyticsurl);

const verbs = {
  httpGet: localInstance.get,
  httpPost: localInstance.post,
  httpPut: localInstance.put,
  httpDelete: localInstance.delete,
  httpGetReportAnalytics: reportAnalyticsInstance.get,
  httpPostReportAnalytics: reportAnalyticsInstance.post,
  httpPutReportAnalytics: reportAnalyticsInstance.put,
  httpDeleteReportAnalytics: reportAnalyticsInstance.delete,
};

export const {
  httpGet,
  httpPost,
  httpPut,
  httpDelete,
  httpGetReportAnalytics,
  httpPostReportAnalytics,
  httpPutReportAnalytics,
  httpDeleteReportAnalytics,
} = verbs || {};

export default verbs;
